// Pure, framework-free currency helpers so the conversion logic can be unit
// tested in isolation (see currency.test.js).

export const CACHE_KEY = "st_currency_rates_v1"
export const PREF_KEY = "st_currency_pref_v1"
export const MAX_AGE_MS = 24 * 60 * 60 * 1000 // refetch rates once a day

// Most-used currencies, surfaced at the top of the picker (after the base
// currency) so people don't have to scroll/search for the obvious ones.
export const COMMON_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "AUD",
  "CAD",
  "JPY",
  "CHF",
  "CNY",
  "SGD",
  "NZD",
  "HKD",
  "INR",
  "AED",
  "THB",
  "MYR",
]

// Order currency entries: base first, then the common list above (in order),
// then everything else alphabetically.
export function sortCurrencies(list, base) {
  const rank = code => {
    if (code === base) return -1
    const i = COMMON_CURRENCIES.indexOf(code)
    return i === -1 ? COMMON_CURRENCIES.length : i
  }
  return [...list].sort((a, b) => {
    const ra = rank(a.code)
    const rb = rank(b.code)
    if (ra !== rb) return ra - rb
    return a.code.localeCompare(b.code)
  })
}

// Convert an `amount` stored in the `base` currency into `selected`.
// Returns the original amount unchanged when no conversion is possible.
export function convert(amount, { base, selected, rates }) {
  if (amount == null || selected === base || !rates) return amount
  const rate = rates[selected.toLowerCase()]
  return rate ? amount * rate : amount
}

// Format an amount (stored in the base currency) as "<grouped number> <CODE>",
// e.g. "5,490,000 IDR" or "305 USD". Falls back to the base currency whenever
// rates aren't ready or the selected currency has no rate.
export function formatMoney(amount, { base, selected, rates, status = "ready" }) {
  const hasRate = selected !== base && rates && rates[selected.toLowerCase()]
  const code = status === "ready" && hasRate ? selected : base
  const value = code === base ? amount : amount * rates[code.toLowerCase()]
  const num = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.round(value || 0)
  )
  return `${num} ${code.toUpperCase()}`
}

// Free, no-key, no-rate-limit exchange rates off a CDN, with a mirror as a
// second try. Returns { rates, date } or null if both are unreachable.
export async function fetchRates(base) {
  const b = base.toLowerCase()
  const urls = [
    `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${b}.json`,
    `https://latest.currency-api.pages.dev/v1/currencies/${b}.json`,
  ]
  for (const url of urls) {
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      const json = await res.json()
      if (json[b]) return { rates: json[b], date: json.date }
    } catch (e) {
      // try the next url
    }
  }
  return null
}

// Fetch a { code: "Full name" } map for every currency, used for search labels.
export async function fetchCurrencyNames() {
  const urls = [
    `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies.min.json`,
    `https://latest.currency-api.pages.dev/v1/currencies.min.json`,
  ]
  for (const url of urls) {
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      return await res.json()
    } catch (e) {
      // try the next url
    }
  }
  return {}
}
